import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check, X, Info, Plus, Calendar, Users, Clock, ChevronDown, Edit, Trash2 } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSuperAdmin } from "../context/SuperAdminContext";
import ReactDOM from 'react-dom';

const createPortalRoot = () => {
  let portalRoot = document.getElementById('tooltip-portal-root');
  if (!portalRoot) {
    portalRoot = document.createElement('div');
    portalRoot.id = 'tooltip-portal-root';
    document.body.appendChild(portalRoot);
  }
  return portalRoot;
};

// Custom hook for outside click detection
const useOutsideClick = (callback) => {
  const ref = useRef();

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [callback]);

  return ref;
};

const SuperAdminRequestHistory = () => {
  const navigate = useNavigate();
  const { fetchLocations, getCompanyByLocation, getDepartmentsByCompany } = useSuperAdmin();

  const [tooltipData, setTooltipData] = useState({ show: false, reason: '', position: { x: 0, y: 0 } });

  // ── Page-level filter states ─────────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedlocationobject, setselectedlocationobject] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isDeptOpen, setIsDeptOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all"); // Changed from "pending" to "all"
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRejectingId, setCurrentRejectingId] = useState(null);
  
  // New state for approval confirmation modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentApprovingId, setCurrentApprovingId] = useState(null);

  // ── Modal states ─────────────────────────────────────────────────────────
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const [showDuplicateConfirmModal, setShowDuplicateConfirmModal] = useState(false);
  const [duplicateEntry, setDuplicateEntry] = useState(null);

  // ── Modal-specific Location / Company states ─────────────────────────────
  const [modalLocations, setModalLocations] = useState([]);
  const [modalLocationId, setModalLocationId] = useState("");
  const [modalLocationName, setModalLocationName] = useState("");
  const [modalCompanies, setModalCompanies] = useState([]);
  const [modalCompanyId, setModalCompanyId] = useState("");
  const [modalCompanyName, setModalCompanyName] = useState("");
  const [modalLoadingLocations, setModalLoadingLocations] = useState(false);
  const [modalLoadingCompanies, setModalLoadingCompanies] = useState(false);
  const [isModalLocationOpen, setIsModalLocationOpen] = useState(false);
  const [isModalCompanyOpen, setIsModalCompanyOpen] = useState(false);

  // ── Modal-specific Department states ─────────────────────────────────────
  const [modalDepartments, setModalDepartments] = useState([]);
  const [modalDepartmentId, setModalDepartmentId] = useState("");
  const [modalDepartmentName, setModalDepartmentName] = useState("");
  const [modalLoadingDepartments, setModalLoadingDepartments] = useState(false);
  const [isModalDepartmentOpen, setIsModalDepartmentOpen] = useState(false);

const [showModifyModal, setShowModifyModal] = useState(false);
const [modifyFormData, setModifyFormData] = useState({
  date: "",
  slot: "",
  numberOfWorkers: "",
  originalDate: "",
  originalSlot: "",
  originalWorkers: "",
});
const [modifyFormErrors, setModifyFormErrors] = useState({});
const [modifyLoading, setModifyLoading] = useState(false);
const [modifyTimeSlots, setModifyTimeSlots] = useState([]);
const [fetchingModifySlots, setFetchingModifySlots] = useState(false);
const [isModifySlotOpen, setIsModifySlotOpen] = useState(false);
const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
const [itemToDelete, setItemToDelete] = useState(null);
const [deleteLoading, setDeleteLoading] = useState(false);

  const [modalFormData, setModalFormData] = useState({
    daySelection: "single",
    date: new Date().toISOString().split("T")[0],
    dateRange: {
      from: new Date().toISOString().split("T")[0],
      to: new Date().toISOString().split("T")[0],
    },
    slot: "",
    numberOfWorkers: "",
  });

  const [modalFormErrors, setModalFormErrors] = useState({});

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [isUTV, setIsUTV] = useState(localStorage.getItem("isUTV"));
  const [showActionsColumn, setShowActionsColumn] = useState(false);

  // Updated status options to include "all"
  const statusOptions = isUTV === "true"
  ? ["all", "pending", "approved", "rejected", "sent-to-hod-approval", "sent-to-hr-approval", "hod-rejected", "hod-edited-and-approved", "hr-approved", "hr-rejected"]
  : ["all", "hr-approved", "hr-rejected", "sent-to-hr-approval"];

  // Helper function to get display text for status
  const getStatusDisplayText = (status) => {
  if (!status) return "Pending";
  const s = status.toLowerCase();
  if (s === "all") return "All Status";
  if (s === "sent-to-hod-approval") return "Sent for HOD Approval";
  if (s === "sent-to-hr-approval") return "Sent for HR Approval";
  if (s === "hod-rejected") return "Rejected by HOD";
  if (s === "hod-edited-and-approved") return "HOD Edited & Approved";
  if (s === "hr-approved") return "Approved by HR";
  if (s === "hr-rejected") return "Rejected by HR";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

  // ── Dropdown outside-click refs ──────────────────────────────────────────
  const locationDropdownRef = useOutsideClick(() => setIsLocationOpen(false));
  const companyDropdownRef = useOutsideClick(() => setIsCompanyOpen(false));
  const departmentDropdownRef = useOutsideClick(() => setIsDeptOpen(false));
  const statusDropdownRef = useOutsideClick(() => setIsStatusOpen(false));
  const slotDropdownRef = useOutsideClick(() => setIsSlotDropdownOpen(false));
  const modalLocationRef = useOutsideClick(() => setIsModalLocationOpen(false));
  const modalCompanyRef = useOutsideClick(() => setIsModalCompanyOpen(false));
  const modalDepartmentRef = useOutsideClick(() => setIsModalDepartmentOpen(false));

  // ── Fetch user data ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.success && response.data?.user) {
          const userData = response.data.user;
          localStorage.setItem("userData", JSON.stringify(userData));
          setIsUTV(String(userData.UTVMember));
          localStorage.setItem("isUTV", String(userData.UTVMember));
          
          setShowActionsColumn(!userData.UTVMember);
          
          if (!userData.UTVMember) {
            setSelectedLocation(userData.CompanyLocation);
            setSelectedCompany(userData.MyCompany);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [BASE_URL]);

  // ── Fetch page-level locations (UTV only) ────────────────────────────────
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (response.data.success && Array.isArray(response.data.locations)) {
          setLocations(response.data.locations);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    if (isUTV === "true") loadLocations();
  }, [BASE_URL, isUTV]);

  // ── Fetch page-level companies when location changes ─────────────────────
  useEffect(() => {
    const loadCompanies = async () => {
      if (!selectedLocation) { setCompanies([]); setSelectedCompany(""); return; }
      try {
        setLoadingCompanies(true);
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(
          `${BASE_URL}/onDemandCompanies/list-companies/${selectedlocationobject._id}`,
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        setCompanies(Array.isArray(response.data?.companies) ? response.data.companies : []);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    if (isUTV === "true") loadCompanies();
  }, [selectedLocation, BASE_URL, isUTV]);

  // ── Reset company when page-level location changes ───────────────────────
  useEffect(() => {
    if (isUTV === "true") { setSelectedCompany(""); setSelectedDepartments([]); }
  }, [selectedLocation, isUTV]);

  // ── Reset departments when company changes ───────────────────────────────
  useEffect(() => {
    setSelectedDepartments([]);
    setSelectedDepartmentIds([]);
  }, [selectedCompany]);

  // ── Fetch departments ────────────────────────────────────────────────────
  useEffect(() => {
    const loadDepartments = async () => {
      const companyIdToFetch = selectedCompany;
      if (!companyIdToFetch) { setDepartments([]); setSelectedDepartments([]); return; }
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.post(
          `${BASE_URL}/onDemandCompanies/list-departments`,
          { companyId: companyIdToFetch },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (response.data && Array.isArray(response.data.departments)) {
          const filtered = response.data.departments.filter(d => d.department && d.department !== "All");
          setDepartments(filtered.map(d => d.department));
          setDepartmentIds(filtered.map(d => d._id));
        } else {
          setDepartments([]);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartments([]);
      }
    };
    loadDepartments();
  }, [selectedCompany, BASE_URL, isUTV]);

  // ── Modal: load locations when modal opens ───────────────────────────────
  const loadModalLocations = async () => {
    try {
      setModalLoadingLocations(true);
      const data = await fetchLocations();
      setModalLocations(Array.isArray(data?.locations) ? data.locations : []);
    } catch (err) {
      console.error("Failed to load modal locations:", err);
      setModalLocations([]);
    } finally {
      setModalLoadingLocations(false);
    }
  };

  // ── Modal: load companies when modal location changes ────────────────────
  useEffect(() => {
    if (!modalLocationId) {
      setModalCompanies([]); setModalCompanyId(""); setModalCompanyName("");
      setModalDepartments([]); setModalDepartmentId(""); setModalDepartmentName("");
      setTimeSlots([]); return;
    }
    const loadModalCompanies = async () => {
      try {
        setModalLoadingCompanies(true);
        const data = await getCompanyByLocation(modalLocationId);
        const list = Array.isArray(data?.companies)
          ? data.companies
          : Array.isArray(data)
          ? data
          : [];
        setModalCompanies(list);
      } catch (err) {
        console.error("Failed to load modal companies:", err);
        setModalCompanies([]);
      } finally {
        setModalLoadingCompanies(false);
      }
    };
    loadModalCompanies();
  }, [modalLocationId]);

  // ── Modal: load departments when modal company changes ───────────────────
  useEffect(() => {
    if (!modalCompanyId) {
      setModalDepartments([]); setModalDepartmentId(""); setModalDepartmentName("");
      setTimeSlots([]); return;
    }
    const loadModalDepartments = async () => {
      try {
        setModalLoadingDepartments(true);
        const data = await getDepartmentsByCompany(modalCompanyId);
        const list = Array.isArray(data?.departments) ? data.departments : [];
        setModalDepartments(list);
      } catch (err) {
        console.error("Failed to load modal departments:", err);
        setModalDepartments([]);
      } finally {
        setModalLoadingDepartments(false);
      }
    };
    loadModalDepartments();
  }, [modalCompanyId]);

  // ── Time slots ───────────────────────────────────────────────────────────
  const fetchTimeSlots = async (department, departmentId) => {
    if (!department) { setTimeSlots([]); return; }
    try {
      setFetchingSlots(true);
      const response = await fetch(`${BASE_URL}/lenskart/department-dropdown`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ department, departmentId }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success && data.timeSlots) {
        const sorted = data.timeSlots.sort(
          (a, b) => new Date(`1970/01/01 ${a.start_time}`) - new Date(`1970/01/01 ${b.start_time}`)
        );
        setTimeSlots(sorted.map(s => `${s.shiftName} (${s.slotStart} - ${s.slotEnd})`));
      } else {
        setTimeSlots([]);
      }
    } catch (error) {
      console.error("Failed to fetch time slots:", error);
      setTimeSlots([]);
    } finally {
      setFetchingSlots(false);
    }
  };

  // ── Fetch history ────────────────────────────────────────────────────────
  const fetchHistory = async () => {
    if (selectedDepartments.length === 0) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      let companyIdToSend = "";
      if (isUTV === "true") {
        companyIdToSend = selectedCompany;
      } else {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        companyIdToSend = userData.MyCompany || selectedCompany;
      }
      const encodedDept = encodeURIComponent(selectedDepartments.join(','));
      const encodedDeptIds = encodeURIComponent(selectedDepartmentIds.join(','));
      let url = `${BASE_URL}/floor-admin/department?department=${encodedDept}&departmentIds=${encodedDeptIds}`;
      
      // Only add status filter if it's not "all"
      if (selectedStatus !== "all") {
        url += `&status=${selectedStatus}`;
      }
      
      if (companyIdToSend) url += `&companyId=${companyIdToSend}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setHistoryData(response.data.data?.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Status update ────────────────────────────────────────────────────────
  const handleStatusUpdate = async (requestId, action, reason = "") => {
  if (!requestId) return;
  setUpdatingRequestId(requestId);
  try {
    const token = localStorage.getItem("superadmintoken");
    const payload = { 
      requestId: requestId,  // Note: single requestId, not requestIds array
      action: action  // 'approve' or 'reject'
    };
    if (action === 'reject' && reason) payload.reason = reason;
    
    const response = await axios.post(
  `${BASE_URL}/floor-admin/hr-action`,
  payload,
  { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
);
if (response.data?.success) {
  setErrorMessage(action === 'approve' ? "Request approved successfully" : "Request rejected successfully");
  setShowSuccessModal(true);
  fetchHistory();
  setTimeout(() => setShowSuccessModal(false), 2000);
} else {
  // HTTP 200 but success: false (e.g. already hr-approved)
  setErrorMessage(response.data?.message || `Failed to ${action} request`);
  setShowErrorModal(true);
  setTimeout(() => setShowErrorModal(false), 3000);
}
  } catch (error) {
    console.error('Error updating status:', error);
    setErrorMessage(error.response?.data?.message || error.message || `Failed to ${action} request`);
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 3000);
  } finally {
    setUpdatingRequestId(null);
  }
};

  // Handle approve click - show confirmation modal
  const handleApproveClick = (requestId) => {
  setCurrentApprovingId(requestId);
  setShowApprovalModal(true);
};

  // Confirm approval
  const handleApproveConfirm = () => {
  if (currentApprovingId) {
    handleStatusUpdate(currentApprovingId, 'approve');
    setShowApprovalModal(false);
    setCurrentApprovingId(null);
  }
};

  // Cancel approval
  const handleApproveCancel = () => {
  setShowApprovalModal(false);
  setCurrentApprovingId(null);
};

  const handleRejectClick = (requestId) => {
  setCurrentRejectingId(requestId);
  setRejectionReason("");
  setShowRejectionModal(true);
};

  const handleRejectConfirm = () => {
  if (rejectionReason.trim().length < 3) { 
    setErrorMessage("Rejection reason must be at least 3 characters long");
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 2000);
    return; 
  }
  if (rejectionReason.trim().length > 100) { 
    setErrorMessage("Rejection reason must not exceed 100 characters");
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 2000);
    return; 
  }
  handleStatusUpdate(currentRejectingId, 'reject', rejectionReason.trim());
  setShowRejectionModal(false);
  setRejectionReason("");
  setCurrentRejectingId(null);
};

  const handleRejectCancel = () => {
  setShowRejectionModal(false);
  setRejectionReason("");
  setCurrentRejectingId(null);
};

  // ── Department multi-select ───────────────────────────────────────────────
  const handleDepartmentToggle = (department) => {
    const index = departments.indexOf(department);
    if (selectedDepartments.includes(department)) {
      setSelectedDepartments(selectedDepartments.filter(d => d !== department));
      setSelectedDepartmentIds(selectedDepartmentIds.filter(id => id !== departmentIds[index]));
    } else {
      setSelectedDepartments([...selectedDepartments, department]);
      setSelectedDepartmentIds([...selectedDepartmentIds, departmentIds[index]]);
    }
  };

  const handleSelectAll = () => {
    if (selectedDepartments.length === departments.length) {
      setSelectedDepartments([]);
      setSelectedDepartmentIds([]);
    } else {
      setSelectedDepartments([...departments]);
      setSelectedDepartmentIds([...departmentIds]);
    }
  };

  const getDisplayText = () => {
    if (selectedDepartments.length === 0) return 'Select Department';
    if (selectedDepartments.length === 1) return selectedDepartments[0];
    if (selectedDepartments.length === departments.length) return 'All Departments';
    return `${selectedDepartments.length} departments selected`;
  };

  // ── Open modal ───────────────────────────────────────────────────────────
  const handleCreateRequirement = () => {
    setModalLocationId("");
    setModalLocationName("");
    setModalCompanyId("");
    setModalCompanyName("");
    setModalCompanies([]);
    setModalDepartments([]);
    setModalDepartmentId("");
    setModalDepartmentName("");
    setTimeSlots([]);
    setModalFormData({
      daySelection: "single",
      date: new Date().toISOString().split("T")[0],
      dateRange: {
        from: new Date().toISOString().split("T")[0],
        to: new Date().toISOString().split("T")[0],
      },
      slot: "",
      numberOfWorkers: "",
    });
    setModalFormErrors({});
    setShowRequirementModal(true);
    loadModalLocations();
  };

  // ── Modal form handlers ──────────────────────────────────────────────────
  const handleModalInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setModalFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setModalFormData(prev => ({ ...prev, [field]: value }));
    }
    if (modalFormErrors[field]) {
      setModalFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleSlotSelect = (slot) => {
    handleModalInputChange("slot", slot);
    setIsSlotDropdownOpen(false);
  };

  const isSlotUpcoming = (date, slotTime) => {
    const now = new Date();
    const slotDate = new Date(date);
    if (slotDate < new Date(now.toISOString().split("T")[0])) return false;
    if (slotDate > new Date(now.toISOString().split("T")[0])) return true;
    if (!slotTime) return false;
    const startTimeStr = slotTime.split(" - ")[0];
    const [time, period] = startTimeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    const slotStartTime = new Date(date);
    slotStartTime.setHours(hours, minutes, 0, 0);
    return slotStartTime >= new Date(now.getTime() + 4 * 60 * 60 * 1000);
  };

  const getDaysBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const validateModalForm = () => {
    const errors = {};
    if (!modalLocationId) errors.modalLocation = "Location is required";
    if (!modalCompanyId) errors.modalCompany = "Company is required";
    if (!modalDepartmentId) errors.modalDepartment = "Department is required";

    if (modalFormData.daySelection === "single") {
      if (!modalFormData.date) {
        errors.date = "Date is required";
      } else {
        const selectedDate = new Date(modalFormData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) errors.date = "Cannot select previous dates";
      }
    } else {
      if (!modalFormData.dateRange.from || !modalFormData.dateRange.to) {
        errors.dateRange = "Both dates are required";
      } else {
        const fromDate = new Date(modalFormData.dateRange.from);
        const toDate = new Date(modalFormData.dateRange.to);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (fromDate < today || toDate < today) errors.dateRange = "Cannot select previous dates";
        if (fromDate > toDate) errors.dateRange = "End date must be after start date";
      }
    }
    if (!modalFormData.slot) errors.slot = "Slot is required";
    if (!modalFormData.numberOfWorkers) {
      errors.numberOfWorkers = "Number of workers is required";
    } else if (parseInt(modalFormData.numberOfWorkers) <= 0) {
      errors.numberOfWorkers = "Number of workers must be positive";
    }
    setModalFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitRequirement = async () => {
    setModalLoading(true);
    try {
      const requestData = modalFormData.daySelection === "single"
        ? {
            isDateRange: false,
            startDate: modalFormData.date,
            slot: modalFormData.slot,
            TotalWorkers: parseInt(modalFormData.numberOfWorkers),
            Department: modalDepartmentName,
            DepartmentId: modalDepartmentId,
            locationId: modalLocationId,
            location: modalLocationName,
            companyId: modalCompanyId,
            company: modalCompanyName,
          }
        : {
            isDateRange: true,
            startDate: modalFormData.dateRange.from,
            endDate: modalFormData.dateRange.to,
            slot: modalFormData.slot,
            TotalWorkers: parseInt(modalFormData.numberOfWorkers),
            Department: modalDepartmentName,
            DepartmentId: modalDepartmentId,
            locationId: modalLocationId,
            location: modalLocationName,
            companyId: modalCompanyId,
            company: modalCompanyName,
          };

      const response = await axios.post(
        `${BASE_URL}/floor-admin/add`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("superadmintoken")}`,
          },
        }
      );

      if (response.data.success) {
        setShowSuccessModal(true);
        setErrorMessage(
          modalFormData.daySelection === "single"
            ? "Requirement added successfully"
            : `${getDaysBetweenDates(modalFormData.dateRange.from, modalFormData.dateRange.to)} requirements added successfully`
        );
        fetchHistory();
        setShowRequirementModal(false);
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw response;
      }
    } catch (response) {
      if (response?.data?.error === "Duplicate entries found") {
        setDuplicateEntry({ message: response.data.message, duplicates: response.data.duplicates });
        setShowDuplicateConfirmModal(true);
      } else {
        setErrorMessage(response?.data?.message || response.data?.error || "Failed to send requirement");
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 3000);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!validateModalForm()) return;
    await submitRequirement();
  };

  const handleDuplicateConfirm = async () => {
    setShowDuplicateConfirmModal(false);
    await submitRequirement();
  };

  const renderSlotDropdownItems = (selectedDate, handleSelect) => (
    <>
      {timeSlots
        .filter(slot => isSlotUpcoming(selectedDate, slot))
        .map((slot, index) => (
          <button key={`enabled-${index}`} type="button" onClick={() => handleSelect(slot)}
            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100">
            {slot}
          </button>
        ))}
      {timeSlots.some(slot => !isSlotUpcoming(selectedDate, slot)) && (
        <div className="border-t border-gray-200 my-1" />
      )}
      {timeSlots
        .filter(slot => !isSlotUpcoming(selectedDate, slot))
        .map((slot, index) => (
          <button key={`disabled-${index}`} type="button" disabled
            className="w-full px-3 py-2 text-left text-gray-400 cursor-not-allowed">
            {slot} (Unavailable)
          </button>
        ))}
    </>
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleApply = () => {
    if (selectedDepartments.length > 0) fetchHistory();
  };

  const isRequestExpired = (requestDate, requestSlot) => {
    if (!requestDate || !requestSlot) return false;
    try {
      const reqDate = new Date(requestDate);
      const timeMatch = requestSlot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const meridiem = timeMatch[3];
        if (meridiem) {
          if (meridiem.toUpperCase() === 'PM' && hours !== 12) hours += 12;
          else if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }
        reqDate.setHours(hours, minutes, 0, 0);
      } else {
        reqDate.setHours(0, 0, 0, 0);
      }
      return new Date() > reqDate;
    } catch { return false; }
  };

  const handleModifyClick = async (record) => {
  setModifyFormData({
    date: record.date,
    slot: record.slot,
    numberOfWorkers: record.TotalWorkers,
    originalDate: record.date,
    originalSlot: record.slot,
    originalWorkers: record.TotalWorkers,
    recordId: record._id,
    department: record.adminDepartment,
    company: record.company,
    location: record.location,
  });
  setModifyFormErrors({});
  setModifyTimeSlots([]);
  setShowModifyModal(true);
  // fetch time slots for this record's department
  if (record.adminDepartment) {
    await fetchModifyTimeSlots(record.adminDepartment, record.DepartmentId);
  }
};

const fetchModifyTimeSlots = async (department, departmentId) => {
  if (!department) { setModifyTimeSlots([]); return; }
  try {
    setFetchingModifySlots(true);
    const response = await fetch(`${BASE_URL}/lenskart/department-dropdown`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ department, departmentId }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.success && data.timeSlots) {
      const sorted = data.timeSlots.sort(
        (a, b) => new Date(`1970/01/01 ${a.start_time}`) - new Date(`1970/01/01 ${b.start_time}`)
      );
      setModifyTimeSlots(sorted.map(s => `${s.shiftName} (${s.slotStart} - ${s.slotEnd})`));
    } else {
      setModifyTimeSlots([]);
    }
  } catch (error) {
    console.error("Failed to fetch modify time slots:", error);
    setModifyTimeSlots([]);
  } finally {
    setFetchingModifySlots(false);
  }
};

const handleModifySubmit = async () => {
  const errors = {};
  if (!modifyFormData.date) errors.date = "Date is required";
  if (!modifyFormData.slot) errors.slot = "Slot is required";
  if (!modifyFormData.numberOfWorkers) errors.numberOfWorkers = "Number of workers is required";
  else if (parseInt(modifyFormData.numberOfWorkers) <= 0) errors.numberOfWorkers = "Must be positive";
  setModifyFormErrors(errors);
  if (Object.keys(errors).length > 0) return;

  if (!isSlotUpcoming(modifyFormData.date, modifyFormData.slot)) {
    setErrorMessage("Cannot modify to a completed time slot");
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 2000);
    return;
  }

  setModifyLoading(true);
  try {
    const token = localStorage.getItem("superadmintoken");
    const response = await axios.put(
  `${BASE_URL}/floor-admin/update`,
  {
    originalDate: modifyFormData.originalDate,
    originalSlot: modifyFormData.originalSlot,
    newDate: modifyFormData.date,
    slot: modifyFormData.slot,
    TotalWorkers: parseInt(modifyFormData.numberOfWorkers),
    Department: modifyFormData.department,
    company: modifyFormData.company,
    location: modifyFormData.location,
  },
  { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
);
    if (response.data.success) {
      setShowModifyModal(false);
      setErrorMessage("Entry updated successfully");
      setShowSuccessModal(true);
      fetchHistory();
      setTimeout(() => setShowSuccessModal(false), 2000);
    } else {
      throw new Error(response.data.message || "Failed to update");
    }
  } catch (error) {
    setErrorMessage(error.response?.data?.message || error.message || "Failed to update requirement");
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 3000);
  } finally {
    setModifyLoading(false);
  }
};

const handleDeleteClick = (record) => {
  setItemToDelete({ 
    date: record.date, 
    slot: record.slot,
    department: record.adminDepartment,
    company: record.company,
    location: record.location,
  });
  setShowDeleteConfirmModal(true);
};

const handleDeleteConfirm = async () => {
  if (!itemToDelete) return;
  setDeleteLoading(true);
  try {
    const token = localStorage.getItem("superadmintoken");
    const response = await axios.delete(`${BASE_URL}/floor-admin/delete`, {
  headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  data: { 
    date: itemToDelete.date, 
    slot: itemToDelete.slot,
    Department: itemToDelete.department,
    company: itemToDelete.company,
    location: itemToDelete.location,
  },
});
    if (response.data.success) {
      setErrorMessage("Entry deleted successfully");
      setShowSuccessModal(true);
      fetchHistory();
      setTimeout(() => setShowSuccessModal(false), 1500);
    } else {
      throw new Error(response.data.message || "Failed to delete");
    }
  } catch (error) {
    setErrorMessage(error.response?.data?.message || error.message || "Failed to delete requirement");
    setShowErrorModal(true);
    setTimeout(() => setShowErrorModal(false), 2000);
  } finally {
    setDeleteLoading(false);
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
  }
};

  const ChevronSVG = ({ open }) => (
    <svg
      className={`h-4 w-4 transform transition-transform ${open ? "rotate-180" : ""}`}
      xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
    >
      <path fillRule="evenodd"
        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        clipRule="evenodd" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0">
      <div className="max-w-7xl mx-auto bg-white shadow-lg rounded-lg p-4">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
  <h2 className="text-2xl font-bold">Request History</h2>
  {isUTV === "true" && (
  <button
    onClick={handleCreateRequirement}
    className="px-6 py-2 text-white rounded-md flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
  >
    <Plus className="h-4 w-4" />
    Submit Worker Requirement
  </button>
)}
</div>

        {/* Page-level Location + Company (UTV only) */}
        {isUTV === "true" && (
          <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative" ref={locationDropdownRef}>
                <label className="text-sm text-gray-600 mb-1 block">Location <span className="text-red-500">*</span></label>
                <button onClick={() => setIsLocationOpen(!isLocationOpen)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white">
                  {selectedLocation || "Select Location"}
                  <ChevronSVG open={isLocationOpen} />
                </button>
                {isLocationOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                    {locations.map((loc, i) => (
                      <button key={i} onClick={() => { setSelectedLocation(loc.name); setselectedlocationobject(loc); setIsLocationOpen(false); }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === loc.name ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                        {loc.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex-1 relative" ref={companyDropdownRef}>
                <label className="text-sm text-gray-600 mb-1 block">Company <span className="text-red-500">*</span></label>
                <button
                  onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen); }}
                  disabled={!selectedLocation || loadingCompanies}
                  className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${
                    !selectedLocation || loadingCompanies ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"
                  }`}
                >
                  {loadingCompanies
                    ? <span className="flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-2" />Loading...</span>
                    : selectedCompany
                    ? companies.find(c => c._id === selectedCompany)?.name || "Select Company"
                    : "Select Company"}
                  <ChevronSVG open={isCompanyOpen} />
                </button>
                {isCompanyOpen && selectedLocation && !loadingCompanies && (
                  <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                    {companies.length > 0
                      ? companies.map(c => (
                          <button key={c._id} onClick={() => { setSelectedCompany(c._id); setIsCompanyOpen(false); }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === c._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            {c.name}
                          </button>
                        ))
                      : <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Department + Status + Apply */}
        <div className="flex flex-row gap-4 mb-4 items-end relative z-20">
  {/* Department */}
  <div className="flex-1 relative" ref={departmentDropdownRef}>
    <label className="text-sm text-gray-600 mb-1 block">Department <span className="text-red-500">*</span></label>
    <button onClick={() => setIsDeptOpen(!isDeptOpen)} disabled={departments.length === 0}
      className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${
        departments.length === 0 ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"
      }`}
    >
      <span className="truncate">{getDisplayText()}</span>
      <ChevronSVG open={isDeptOpen} />
    </button>
    {isDeptOpen && departments.length > 0 && (
      <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
        <div className="px-3 py-2 cursor-pointer flex items-center border-b border-gray-200 bg-gray-50 hover:bg-gray-100" onClick={handleSelectAll}>
          <input type="checkbox" checked={selectedDepartments.length === departments.length && departments.length > 0}
            onChange={handleSelectAll} className="w-4 h-4 mr-2 cursor-pointer" onClick={e => e.stopPropagation()} />
          <span className="text-sm font-medium">
            {selectedDepartments.length === departments.length && departments.length > 0 ? "Deselect All" : "Select All"}
          </span>
        </div>
        {departments.map((dept, i) => (
          <div key={i} className="px-3 py-2 cursor-pointer flex items-center hover:bg-gray-100" onClick={() => handleDepartmentToggle(dept)}>
            <input type="checkbox" checked={selectedDepartments.includes(dept)}
              onChange={() => handleDepartmentToggle(dept)} className="w-4 h-4 mr-2 cursor-pointer" onClick={e => e.stopPropagation()} />
            <span className="text-sm">{dept}</span>
          </div>
        ))}
      </div>
    )}
  </div>

          {/* Status Dropdown - Now optional */}
          <div className="flex-1 relative" ref={statusDropdownRef}>
    <label className="text-sm text-gray-600 mb-1 block">Status (Optional)</label>
    <button onClick={() => setIsStatusOpen(!isStatusOpen)}
      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white">
      <span className="capitalize">{getStatusDisplayText(selectedStatus)}</span>
      <ChevronSVG open={isStatusOpen} />
    </button>
    {isStatusOpen && (
      <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded-md py-1 border border-gray-300">
        {statusOptions.map((s, i) => (
          <button key={i} onClick={() => { setSelectedStatus(s); setIsStatusOpen(false); }}
            className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 capitalize ${selectedStatus === s ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
            {getStatusDisplayText(s)}
          </button>
        ))}
      </div>
    )}
  </div>

          {/* Apply */}
          <div className="flex-shrink-0">
    <button onClick={handleApply}
      disabled={selectedDepartments.length === 0 || historyLoading || departments.length === 0}
      className="px-6 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap">
      {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
    </button>
  </div>
</div>

{departments.length === 0 && (
  <p className="text-xs text-gray-500 mb-4">
    {isUTV === "true" ? "Select a company to load departments" : "Loading departments..."}
  </p>
)}

        {/* History Table */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Request History</h3>
  <div className="w-full overflow-y-auto">
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 bg-white shadow-md z-10">
                <tr className="bg-gray-100">
                  <th className="p-3 text-left">Requested On</th>
                  <th className="p-3 text-center">Department</th>
                  <th className="p-3 text-center">Requested Date</th>
                  <th className="p-3 text-center">Requested Slot</th>
                  <th className="p-3 text-center">Total Workers</th>
                  <th className="p-3 text-center">Status</th>
                  {showActionsColumn && <th className="p-3 text-center w-40">Actions</th>}
                </tr>
              </thead>
              <tbody>
                
                {historyLoading ? (
                  <tr><td colSpan={showActionsColumn ? 7 : 6} className="text-center p-4"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                ) : historyData.length > 0 ? (
                  (isUTV === "true" 
  ? historyData 
  : historyData.filter(record => 
    ["hr-approved", "hr-rejected", "sent-to-hr-approval"].includes(record.status)
  )
).map((record, index) => (
                    <tr key={record._id || index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 text-left">{formatDateTime(record.created_at)}</td>
<td className="p-3 text-center">{record.adminDepartment || '-'}</td>
                      <td className="p-3 text-center">{formatDate(record.date)}</td>
                      <td className="p-3 text-center">{record.slot || '-'}</td>
                      <td className="p-3 text-center">{record.TotalWorkers || '-'}</td>
                      <td className="p-3 text-center">
  <div className="flex items-center justify-center gap-2 relative">
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
  record.status === 'approved' || record.status?.toLowerCase() === 'hr-approved'
    ? 'bg-green-100 text-green-800'
    : record.status === 'rejected' || record.status === 'hod-rejected' || record.status?.toLowerCase() === 'hr-rejected'
    ? 'bg-red-100 text-red-800'
    : record.status === 'sent-to-hod-approval'
    ? 'bg-purple-100 text-purple-800'
    : record.status === 'sent-to-hr-approval'
    ? 'bg-blue-100 text-blue-800'
    : record.status?.toLowerCase() === 'hod-edited-and-approved'
    ? 'bg-teal-100 text-teal-800'
    : 'bg-yellow-100 text-yellow-800'
}`}>
      {record.status === 'sent-to-hod-approval'
  ? 'Sent to HOD Approval'
  : record.status === 'sent-to-hr-approval'
  ? 'Sent to HR Approval'
  : record.status === 'hod-rejected'
  ? 'Rejected by HOD'
  : record.status?.toLowerCase() === 'hr-approved'
  ? 'Approved by HR'
  : record.status?.toLowerCase() === 'hr-rejected'
  ? 'Rejected by HR'
  : record.status?.toLowerCase() === 'hod-edited-and-approved'
  ? 'HOD Edited & Approved'
  : record.status
  ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
  : 'Pending'}
    </span>
    {(record.status === 'rejected' || record.status === 'hod-rejected' || record.status?.toLowerCase() === 'hr-rejected') && record.reason && (
      <div 
        className="relative inline-block"
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltipData({
            show: true,
            reason: record.reason,
            position: {
              x: rect.left + rect.width / 2,
              y: rect.top
            }
          });
        }}
        onMouseLeave={() => setTooltipData({ show: false, reason: '', position: { x: 0, y: 0 } })}
      >
        <Info className="h-4 w-4 text-red-600 cursor-help" />
      </div>
    )}
  </div>
</td>
                      {showActionsColumn && (
  <td className="p-3">
  <div className="flex justify-end items-center gap-2">
    {updatingRequestId === record._id || deleteLoading ? (
      <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
    ) : (
  <>
    {record.status?.toLowerCase() !== 'approved' && record.status?.toLowerCase() !== 'hr-approved' && (
      <button
        onClick={() => handleApproveClick(record._id)}
        className="p-2 rounded-full bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
        title="Approve"
      >
        <Check className="h-4 w-4" />
      </button>
    )}
    {record.status?.toLowerCase() !== 'rejected' && 
 record.status?.toLowerCase() !== 'hr-rejected' && (
  <button
    onClick={() => !record.status?.toLowerCase().includes('hr-approved') && handleRejectClick(record._id)}
    disabled={record.status?.toLowerCase() === 'hr-approved'}
    className={`p-2 rounded-full transition-colors ${
      record.status?.toLowerCase() === 'hr-approved'
        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
        : 'bg-red-100 hover:bg-red-200 text-red-700'
    }`}
    title={record.status?.toLowerCase() === 'hr-approved' ? 'Cannot reject an HR approved request' : 'Reject'}
  >
    <X className="h-4 w-4" />
  </button>
)}
  </>
)}
  </div>
</td>
)}
                     </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showActionsColumn ? 7 : 6} className="text-center p-4 text-gray-500">
                      {selectedDepartments.length > 0 ? 'No history found' : 'Select departments to view history'}
                     </td>
                   </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Submit Worker Requirement Modal */}
      {showRequirementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Submit Worker Requirement{modalDepartmentName ? ` for ${modalDepartmentName}` : ""}
            </h3>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {/* Modal Location Dropdown */}
              <div className="space-y-1" ref={modalLocationRef}>
                <label className="block text-sm font-medium text-gray-700">
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsModalLocationOpen(!isModalLocationOpen)}
                    disabled={modalLoadingLocations}
                    className={`w-full px-3 py-2 border rounded-md text-left flex justify-between items-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      modalFormErrors.modalLocation ? "border-red-500" : "border-gray-300"
                    } ${modalLoadingLocations ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
                  >
                    {modalLoadingLocations
                      ? <span className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Loading locations...</span>
                      : modalLocationName || "Select Location"}
                    <ChevronDown className={`h-4 w-4 transition-transform ${isModalLocationOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isModalLocationOpen && !modalLoadingLocations && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {modalLocations.length > 0
                        ? modalLocations.map((loc, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => {
                                setModalLocationId(loc._id);
                                setModalLocationName(loc.name);
                                setModalCompanyId("");
                                setModalCompanyName("");
                                setIsModalLocationOpen(false);
                                if (modalFormErrors.modalLocation) {
                                  setModalFormErrors(prev => ({ ...prev, modalLocation: "" }));
                                }
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${
                                modalLocationId === loc._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                              }`}
                            >
                              {loc.name}
                            </button>
                          ))
                        : <div className="px-4 py-2 text-sm text-gray-500">No locations found</div>}
                    </div>
                  )}
                </div>
                {modalFormErrors.modalLocation && (
                  <p className="text-red-500 text-xs mt-1">{modalFormErrors.modalLocation}</p>
                )}
              </div>

              {/* Modal Company Dropdown */}
              <div className="space-y-1" ref={modalCompanyRef}>
                <label className="block text-sm font-medium text-gray-700">
                  Company <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { if (modalLocationId && !modalLoadingCompanies) setIsModalCompanyOpen(!isModalCompanyOpen); }}
                    disabled={!modalLocationId || modalLoadingCompanies}
                    className={`w-full px-3 py-2 border rounded-md text-left flex justify-between items-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      modalFormErrors.modalCompany ? "border-red-500" : "border-gray-300"
                    } ${!modalLocationId || modalLoadingCompanies ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
                  >
                    {modalLoadingCompanies
                      ? <span className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Loading companies...</span>
                      : modalCompanyName || (modalLocationId ? "Select Company" : "Select a location first")}
                    <ChevronDown className={`h-4 w-4 transition-transform ${isModalCompanyOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isModalCompanyOpen && modalLocationId && !modalLoadingCompanies && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {modalCompanies.length > 0
                        ? modalCompanies.map((c, i) => (
                            <button
                              key={c._id || i}
                              type="button"
                              onClick={() => {
                                setModalCompanyId(c._id);
                                setModalCompanyName(c.name);
                                setModalDepartmentId("");
                                setModalDepartmentName("");
                                setTimeSlots([]);
                                setIsModalCompanyOpen(false);
                                if (modalFormErrors.modalCompany) {
                                  setModalFormErrors(prev => ({ ...prev, modalCompany: "" }));
                                }
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${
                                modalCompanyId === c._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                              }`}
                            >
                              {c.name}
                            </button>
                          ))
                        : <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>}
                    </div>
                  )}
                </div>
                {modalFormErrors.modalCompany && (
                  <p className="text-red-500 text-xs mt-1">{modalFormErrors.modalCompany}</p>
                )}
              </div>

              {/* Modal Department Dropdown */}
              <div className="space-y-1" ref={modalDepartmentRef}>
                <label className="block text-sm font-medium text-gray-700">
                  Department <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { if (modalCompanyId && !modalLoadingDepartments) setIsModalDepartmentOpen(!isModalDepartmentOpen); }}
                    disabled={!modalCompanyId || modalLoadingDepartments}
                    className={`w-full px-3 py-2 border rounded-md text-left flex justify-between items-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      modalFormErrors.modalDepartment ? "border-red-500" : "border-gray-300"
                    } ${!modalCompanyId || modalLoadingDepartments ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
                  >
                    {modalLoadingDepartments
                      ? <span className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Loading departments...</span>
                      : modalDepartmentName || (modalCompanyId ? "Select Department" : "Select a company first")}
                    <ChevronDown className={`h-4 w-4 transition-transform ${isModalDepartmentOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isModalDepartmentOpen && modalCompanyId && !modalLoadingDepartments && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {modalDepartments.length > 0
                        ? modalDepartments.map((dept) => (
                            <button
                              key={dept._id}
                              type="button"
                              onClick={() => {
                                setModalDepartmentId(dept._id);
                                setModalDepartmentName(dept.department);
                                setIsModalDepartmentOpen(false);
                                setTimeSlots([]);
                                handleModalInputChange("slot", "");
                                fetchTimeSlots(dept.department, dept._id);
                                if (modalFormErrors.modalDepartment) {
                                  setModalFormErrors(prev => ({ ...prev, modalDepartment: "" }));
                                }
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${
                                modalDepartmentId === dept._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                              }`}
                            >
                              {dept.department}
                            </button>
                          ))
                        : <div className="px-4 py-2 text-sm text-gray-500">No departments found for this company</div>}
                    </div>
                  )}
                </div>
                {modalFormErrors.modalDepartment && (
                  <p className="text-red-500 text-xs mt-1">{modalFormErrors.modalDepartment}</p>
                )}
              </div>

              <div className="border-t border-gray-200 pt-2" />

              {/* Day Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Day Selection *</label>
                <div className="flex items-center space-x-4">
                  <label className="inline-flex items-center">
                    <input type="radio" name="daySelection" className="form-radio h-4 w-4 text-blue-600"
                      checked={modalFormData.daySelection === "single"}
                      onChange={() => handleModalInputChange("daySelection", "single")} />
                    <span className="ml-2">Single Day</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input type="radio" name="daySelection" className="form-radio h-4 w-4 text-blue-600"
                      checked={modalFormData.daySelection === "multiple"}
                      onChange={() => handleModalInputChange("daySelection", "multiple")} />
                    <span className="ml-2">Multiple Days</span>
                  </label>
                </div>
              </div>

              {/* Date Selection */}
              {modalFormData.daySelection === "single" ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Calendar className="h-4 w-4 inline mr-1" />Date *
                  </label>
                  <input type="date" value={modalFormData.date}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => {
                      handleModalInputChange("date", e.target.value);
                      if (e.target.value === new Date().toISOString().split("T")[0] && modalFormData.slot) {
                        if (!isSlotUpcoming(e.target.value, modalFormData.slot)) {
                          setErrorMessage("Selected slot has already completed");
                          setShowErrorModal(true);
                          setTimeout(() => setShowErrorModal(false), 2000);
                          handleModalInputChange("slot", "");
                        }
                      }
                    }}
                    onClick={(e) => e.target.showPicker()}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${modalFormErrors.date ? "border-red-500" : "border-gray-300"}`}
                  />
                  {modalFormErrors.date && <p className="text-red-500 text-xs mt-1">{modalFormErrors.date}</p>}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <Calendar className="h-4 w-4 inline mr-1" />From Date *
                    </label>
                    <input type="date" value={modalFormData.dateRange.from}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        handleModalInputChange("dateRange.from", e.target.value);
                        if (e.target.value === new Date().toISOString().split("T")[0] && modalFormData.slot) {
                          if (!isSlotUpcoming(e.target.value, modalFormData.slot)) {
                            setErrorMessage("Selected slot has already completed");
                            setShowErrorModal(true);
                            setTimeout(() => setShowErrorModal(false), 2000);
                            handleModalInputChange("slot", "");
                          }
                        }
                      }}
                      onClick={(e) => e.target.showPicker()}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${modalFormErrors.dateRange ? "border-red-500" : "border-gray-300"}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <Calendar className="h-4 w-4 inline mr-1" />To Date *
                    </label>
                    <input type="date" value={modalFormData.dateRange.to}
                      min={modalFormData.dateRange.from}
                      onChange={(e) => handleModalInputChange("dateRange.to", e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${modalFormErrors.dateRange ? "border-red-500" : "border-gray-300"}`}
                    />
                  </div>
                  {modalFormErrors.dateRange && (
                    <p className="text-red-500 text-xs mt-1 col-span-2">{modalFormErrors.dateRange}</p>
                  )}
                </div>
              )}

              {/* Slot Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Clock className="h-4 w-4 inline mr-1" />Slot *
                </label>
                <div className="relative" ref={slotDropdownRef}>
                  <button type="button"
                    onClick={() => {
                      if (modalDepartmentId) {
                        setIsSlotDropdownOpen(!isSlotDropdownOpen);
                      }
                    }}
                    disabled={!modalDepartmentId}
                    className={`w-full h-[42px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between ${
                      modalFormErrors.slot ? "border-red-500" : "border-gray-300"
                    } ${!modalFormData.slot ? "text-gray-500" : "text-gray-900"} ${!modalDepartmentId ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  >
                    <span>{modalFormData.slot || "Select Slot"}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isSlotDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isSlotDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                      {timeSlots.length > 0
                        ? renderSlotDropdownItems(
                            modalFormData.daySelection === "single" ? modalFormData.date : modalFormData.dateRange.from,
                            handleSlotSelect
                          )
                        : <div className="px-3 py-2 text-gray-500">{fetchingSlots ? "Loading slots..." : "No slots available"}</div>}
                    </div>
                  )}
                </div>
                {modalFormErrors.slot && <p className="text-red-500 text-xs mt-1">{modalFormErrors.slot}</p>}
              </div>

              {/* Number of Workers */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Users className="h-4 w-4 inline mr-1" />Number of Workers *
                </label>
                <input type="number" min="1" value={modalFormData.numberOfWorkers}
                  onChange={(e) => handleModalInputChange("numberOfWorkers", e.target.value)}
                  placeholder="Enter number"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${modalFormErrors.numberOfWorkers ? "border-red-500" : "border-gray-300"}`}
                />
                {modalFormErrors.numberOfWorkers && (
                  <p className="text-red-500 text-xs mt-1">{modalFormErrors.numberOfWorkers}</p>
                )}
              </div>

              {/* Form Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowRequirementModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    modalLoading ||
                    !modalFormData.slot ||
                    !isSlotUpcoming(
                      modalFormData.daySelection === "single" ? modalFormData.date : modalFormData.dateRange.from,
                      modalFormData.slot
                    ) ||
                    !modalDepartmentId
                  }
                  className={`px-6 py-2 text-white rounded-md flex items-center gap-2 ${
                    modalLoading ||
                    !modalFormData.slot ||
                    !isSlotUpcoming(
                      modalFormData.daySelection === "single" ? modalFormData.date : modalFormData.dateRange.from,
                      modalFormData.slot
                    ) ||
                    !modalDepartmentId
                      ? "bg-blue-300 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Submit Requirement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modify Modal */}
{showModifyModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
      <h3 className="text-lg font-semibold mb-4">Modify Worker Requirement</h3>

      <div className="bg-gray-50 p-3 rounded-md mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Original Values:</h4>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><span className="text-gray-500">Date:</span><p className="font-medium">{formatDate(modifyFormData.originalDate)}</p></div>
          <div><span className="text-gray-500">Slot:</span><p className="font-medium">{modifyFormData.originalSlot}</p></div>
          <div><span className="text-gray-500">Workers:</span><p className="font-medium">{modifyFormData.originalWorkers}</p></div>
        </div>
      </div>

      <div className="space-y-4">
        {/* New Date */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            <Calendar className="h-4 w-4 inline mr-1" />New Date *
          </label>
          <input type="date" value={modifyFormData.date}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => {
              setModifyFormData(prev => ({ ...prev, date: e.target.value }));
              if (modifyFormErrors.date) setModifyFormErrors(prev => ({ ...prev, date: "" }));
            }}
            onClick={(e) => e.target.showPicker()}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${modifyFormErrors.date ? "border-red-500" : "border-gray-300"}`}
          />
          {modifyFormErrors.date && <p className="text-red-500 text-xs">{modifyFormErrors.date}</p>}
        </div>

        {/* New Slot */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            <Clock className="h-4 w-4 inline mr-1" />New Slot *
          </label>
          <div className="relative">
            <button type="button"
              onClick={() => setIsModifySlotOpen(!isModifySlotOpen)}
              className={`w-full px-3 py-2 border rounded-md text-left flex justify-between items-center text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${modifyFormErrors.slot ? "border-red-500" : "border-gray-300"} ${!modifyFormData.slot ? "text-gray-500" : "text-gray-900"}`}
            >
              <span>{modifyFormData.slot || "Select Slot"}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isModifySlotOpen ? "rotate-180" : ""}`} />
            </button>
            {isModifySlotOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                {modifyTimeSlots.length > 0 ? (
                  <>
                    {modifyTimeSlots.filter(s => isSlotUpcoming(modifyFormData.date, s)).map((slot, i) => (
                      <button key={`e-${i}`} type="button"
                        onClick={() => { setModifyFormData(prev => ({ ...prev, slot })); setIsModifySlotOpen(false); if (modifyFormErrors.slot) setModifyFormErrors(prev => ({ ...prev, slot: "" })); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100">{slot}</button>
                    ))}
                    {modifyTimeSlots.some(s => !isSlotUpcoming(modifyFormData.date, s)) && <div className="border-t border-gray-200 my-1" />}
                    {modifyTimeSlots.filter(s => !isSlotUpcoming(modifyFormData.date, s)).map((slot, i) => (
                      <button key={`d-${i}`} type="button" disabled
                        className="w-full px-3 py-2 text-left text-sm text-gray-400 cursor-not-allowed">{slot} (Unavailable)</button>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">{fetchingModifySlots ? "Loading slots..." : "No slots available"}</div>
                )}
              </div>
            )}
          </div>
          {modifyFormErrors.slot && <p className="text-red-500 text-xs">{modifyFormErrors.slot}</p>}
        </div>

        {/* New Workers */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            <Users className="h-4 w-4 inline mr-1" />New Number of Workers *
          </label>
          <input type="number" min="1" value={modifyFormData.numberOfWorkers}
            onChange={(e) => { setModifyFormData(prev => ({ ...prev, numberOfWorkers: e.target.value })); if (modifyFormErrors.numberOfWorkers) setModifyFormErrors(prev => ({ ...prev, numberOfWorkers: "" })); }}
            placeholder="Enter number"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${modifyFormErrors.numberOfWorkers ? "border-red-500" : "border-gray-300"}`}
          />
          {modifyFormErrors.numberOfWorkers && <p className="text-red-500 text-xs">{modifyFormErrors.numberOfWorkers}</p>}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => setShowModifyModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={handleModifySubmit} disabled={modifyLoading}
            className={`px-6 py-2 text-white rounded-md flex items-center gap-2 ${modifyLoading ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"}`}>
            {modifyLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Requirement
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Delete Confirm Modal */}
{showDeleteConfirmModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 text-center max-w-sm w-full mx-4">
      <div className="text-red-500 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm Deletion</h3>
      <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this slot?</p>
      <div className="flex justify-center gap-4">
        <button onClick={() => { setShowDeleteConfirmModal(false); setItemToDelete(null); }}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={handleDeleteConfirm} disabled={deleteLoading}
          className={`px-4 py-2 text-white rounded-md flex items-center gap-2 ${deleteLoading ? "bg-red-300 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"}`}>
          {deleteLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  </div>
)}

      {/* Approval Confirmation Modal */}
      {showApprovalModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">Confirm Approval</h3>
      <p className="text-gray-600 mb-6">Are you sure you want to approve this request?</p>
      <div className="flex justify-end gap-3">
        <button
          onClick={handleApproveCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleApproveConfirm}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Approve
        </button>
      </div>
    </div>
  </div>
)}

      {/* Duplicate Entry Modal */}
      {showDuplicateConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4">
            <div className="text-yellow-500 mb-4 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">Duplicate Entry Found</h3>
            <p className="text-sm text-gray-600 mb-4 text-center">{duplicateEntry?.message}</p>
            <div className="max-h-60 overflow-y-auto mb-4">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left border">Date</th>
                    <th className="p-2 text-left border">Slot</th>
                    <th className="p-2 text-left border">Existing Workers</th>
                    <th className="p-2 text-center border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateEntry?.duplicates?.map((dup, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-2 border">{formatDate(dup.date)}</td>
                      <td className="p-2 border">{dup.slot}</td>
                      <td className="p-2 border">{dup.existingWorkers}</td>
                      <td className="p-2 border text-center">
                        <button onClick={() => setShowDuplicateConfirmModal(false)}
                          className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs">
                          OK
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mb-4 text-center">Please choose a different slot/date.</p>
            <div className="flex justify-center space-x-4">
              <button onClick={() => setShowDuplicateConfirmModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-md w-full mx-4">
            <div className="text-green-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">{errorMessage}</h3>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-md w-full mx-4">
            <div className="text-red-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-sm text-gray-600">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Rejection Reason</h3>
            <div className="mb-4">
              <label className="block text-sm text-gray-700 mb-2">
                Please provide a reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => { if (e.target.value.length <= 100) setRejectionReason(e.target.value); }}
                placeholder="Enter rejection reason (min 3 characters, max 100 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows="4"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">{rejectionReason.length}/100</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={handleRejectCancel}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button onClick={handleRejectConfirm} disabled={rejectionReason.trim().length < 3}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors">
                Reject
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Tooltip Portal */}
{tooltipData.show && ReactDOM.createPortal(
  <div 
    className="fixed px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-lg whitespace-normal w-64 z-[9999]"
    style={{
      left: `${tooltipData.position.x}px`,
      top: `${tooltipData.position.y - 10}px`,
      transform: 'translateX(-50%) translateY(-100%)',
      pointerEvents: 'none'
    }}
  >
    <div className="font-semibold mb-1 text-gray-300">Rejection Reason:</div>
    <div className="text-gray-100 break-words">{tooltipData.reason}</div>
    <div 
      className="absolute w-0 h-0"
      style={{
        bottom: '-6px',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #111827'
      }}
    />
  </div>,
  document.body
)}
    </div>
  );
};

export default SuperAdminRequestHistory;